import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { listCommentsForModeration, moderateComment } from "@/lib/comments.functions";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, X, RotateCcw, MessageCircle, ExternalLink, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDateTimeBR } from "@/lib/datetime";

export const Route = createFileRoute("/_authenticated/admin/comentarios")({
  head: () => ({
    meta: [{ title: "Moderação de comentários · Admin" }, { name: "robots", content: "noindex" }],
  }),
  component: AdminCommentsPage,
});

type Status = "pending" | "approved" | "removed" | "all";

const TABS: { key: Status; label: string }[] = [
  { key: "pending", label: "Pendentes" },
  { key: "approved", label: "Aprovados" },
  { key: "removed", label: "Removidos" },
  { key: "all", label: "Todos" },
];

function AdminCommentsPage() {
  const [tab, setTab] = useState<Status>("pending");
  const qc = useQueryClient();
  const listFn = useServerFn(listCommentsForModeration);
  const modFn = useServerFn(moderateComment);

  const { data, isLoading } = useQuery({
    queryKey: ["moderation-comments", tab],
    queryFn: () => listFn({ data: { status: tab } }),
  });

  const act = useMutation({
    mutationFn: (v: { id: string; action: "approve" | "remove" | "restore" }) => modFn({ data: v }),
    onSuccess: (_r, v) => {
      toast.success(
        v.action === "approve"
          ? "Comentário aprovado."
          : v.action === "remove"
            ? "Comentário removido."
            : "Comentário restaurado.",
      );
      qc.invalidateQueries({ queryKey: ["moderation-comments"] });
      qc.invalidateQueries({ queryKey: ["pending-comments-count"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Erro ao moderar."),
  });

  return (
    <div className="space-y-6">
      <header className="flex items-center gap-3">
        <MessageCircle className="h-6 w-6 text-primary" />
        <div>
          <h1 className="font-serif text-2xl font-semibold text-primary">
            Moderação de comentários
          </h1>
          <p className="text-sm text-muted-foreground">
            Revise os comentários enviados pelos membros antes de publicá-los.
          </p>
        </div>
      </header>

      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={cn(
              "rounded-full border px-3 py-1.5 text-sm font-medium transition-colors",
              tab === t.key
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border text-muted-foreground hover:text-primary",
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Altura mínima igual nos dois estados: a página não "pula" quando a
          lista de comentários carrega. */}
      <div className="min-h-[60vh]">
        {isLoading ? (
          <div
            className="min-h-[60vh] animate-pulse rounded-xl bg-secondary"
            role="status"
            aria-label="Carregando"
          />
        ) : (data?.length ?? 0) === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-12 text-center text-muted-foreground">
            Nenhum comentário nesta lista.
          </div>
        ) : (
          <div className="space-y-3">
            {data!.map((c) => (
              <article key={c.id} className="rounded-xl border border-border bg-card p-4">
                <header className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-secondary text-sm font-semibold text-primary">
                      {c.avatarUrl ? (
                        <img
                          src={c.avatarUrl}
                          alt={c.authorName}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <span>{c.authorName.slice(0, 1).toUpperCase()}</span>
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-foreground">{c.authorName}</p>
                      <time className="text-xs text-muted-foreground" dateTime={c.createdAt}>
                        {formatDateTimeBR(c.createdAt, {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </time>
                    </div>
                  </div>
                  <span
                    className={cn(
                      "rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
                      c.status === "pending" && "bg-gold/15 text-gold",
                      c.status === "approved" &&
                        "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
                      c.status === "removed" && "bg-destructive/15 text-destructive",
                    )}
                  >
                    {c.status === "pending"
                      ? "Pendente"
                      : c.status === "approved"
                        ? "Aprovado"
                        : "Removido"}
                  </span>
                </header>

                <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">
                  {c.content}
                </p>

                <footer className="mt-4 flex flex-wrap items-center justify-between gap-3">
                  <Link
                    to="/ensinos/$id"
                    params={{ id: c.teachingId }}
                    className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                  >
                    <ExternalLink className="h-3 w-3" /> {c.teachingTitle}
                  </Link>
                  <div className="flex flex-wrap gap-2">
                    {c.status !== "approved" && (
                      <Button
                        size="sm"
                        onClick={() =>
                          act.mutate({
                            id: c.id,
                            action: c.status === "removed" ? "restore" : "approve",
                          })
                        }
                        disabled={act.isPending}
                      >
                        {act.isPending ? (
                          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                        ) : c.status === "removed" ? (
                          <RotateCcw className="mr-1 h-3 w-3" />
                        ) : (
                          <Check className="mr-1 h-3 w-3" />
                        )}
                        {c.status === "removed" ? "Restaurar" : "Aprovar"}
                      </Button>
                    )}
                    {c.status !== "removed" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => act.mutate({ id: c.id, action: "remove" })}
                        disabled={act.isPending}
                      >
                        <X className="mr-1 h-3 w-3" /> Remover
                      </Button>
                    )}
                  </div>
                </footer>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
